import { ConfirmationDialog } from "common/styleguide/ConfirmationDialog";
import { RadioButtonGroup } from "common/styleguide/RadioGroup";
import { User } from "data/records";
import { localeActions, localeSelectors } from "data/reducers/locale";
import { userActions } from "data/resources/users";
import { Map, OrderedMap } from "immutable";
import * as currenUserActions from "lib/api/currentUser";
import fetchJSON from "lib/fetchJSON";
import { availableLocales, commonCTAs, fm } from "lib/localization";
import { userIsAdmin } from "lib/utils";
import React, { Suspense } from "react";
import { createIntl, createIntlCache, defineMessages } from "react-intl";
import { connect } from "react-redux";
import styles from "./ChangeLocaleModal.scss";
import flagFR from "./france-icon.svg";
import flagDE from "./germany-icon.svg";
import flagIT from "./it-icon.svg";
import flagJP from "./japan-icon.svg";
import flagNL from "./ned-icon.svg";
import flagES from "./spain-icon.svg";
import flagUK from "./uk-icon.svg";
import flagUS from "./us-icon.svg";

const CustomFormattedMessage = React.lazy(() => import("common/CustomFormattedMessage"));

export const CHANGE_LOCALE_MODAL = "CHANGE_LOCALE_MODAL";
export const LOCALE_SWITCHER_PREF = "locale_switcher_onboarding_dismissed";
export const LOCALE_BUMP_PREF = "show_locale_bump";

const messages = defineMessages({
  day: {
    id: "ChangeLocaleModal.day",
    defaultMessage: "Day",
  },
  month: {
    id: "ChangeLocaleModal.month",
    defaultMessage: "Month",
  },
  year: {
    id: "ChangeLocaleModal.year",
    defaultMessage: "Year",
  },
  locAlert: {
    id: "ChangeLocaleModal.locAlert",
    defaultMessage: "Ride with GPS is now available in more languages. Please select your preferred language below.",
  },
  title: {
    id: "ChangeLocaleModal.title",
    defaultMessage: "Change Language",
  },
  dateFormat: {
    id: "ChangeLocaleModal.dateFormat",
    defaultMessage: "Date Format",
  },
  metricUnits: {
    id: "ChangeLocaleModal.metricUnits",
    defaultMessage: "Use metric units?",
  },
});

const FlagButton = ({ flag, children }) => (
  <span className={styles.FlagButton}>
    <span>{children}</span>
    <div className={styles.flag}>
      {flag === flagUK && (
        <>
          <img src={flagUS} />
          <span className={styles.spacer}>/</span>
        </>
      )}{" "}
      <img src={flag} />
    </div>
  </span>
);

const leftLangs = OrderedMap({
  en: <FlagButton flag={flagUK}>English</FlagButton>,
  de: <FlagButton flag={flagDE}>Deutsch</FlagButton>,
  es: <FlagButton flag={flagES}>Español</FlagButton>,
  it: <FlagButton flag={flagIT}>Italiano</FlagButton>,
}).filter((v, k) => availableLocales.includes(k));

const _rightLangs = OrderedMap({
  fr: <FlagButton flag={flagFR}>Français</FlagButton>,
  nl: <FlagButton flag={flagNL}>Nederlands</FlagButton>,
  ja: <FlagButton flag={flagJP}>日本語</FlagButton>,
}).filter((v, k) => availableLocales.includes(k));

const rightLangs = userIsAdmin
  ? _rightLangs.set("mz", <FlagButton flag={flagUS}>ḖḖƞɠŀīşħ (admin)</FlagButton>)
  : _rightLangs;

export class _ChangeLocaleModal extends React.PureComponent {
  constructor(props) {
    super(props);
    const {
      language,
      navigatorLanguage,
      currentUser: { preferences },
    } = props;
    const metricUnits = preferences.get("metric_units", false);
    const dateFormat = preferences.get("date_format", "dmy");

    this.state = {
      language,
      catalog: Map(),
      metricUnits,
      dateFormat,
      initialMetricUnits: metricUnits, // keep these around so we can force refresh on change
      initialDateFormat: dateFormat,
    };
    // have to keep the locale bump info in localstorage because changing locale
    // causes a remount of the globalnav, so we can't persist it in state at that level
    if (navigatorLanguage) window.localStorage.setItem(LOCALE_BUMP_PREF, "true");
  }

  componentDidMount() {
    const { language, catalog } = this.state;

    if (language !== "en") {
      import("langs/" + language + ".json").then((module) => {
        this.setState({ catalog: catalog.set(language, module.default) });
      });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { language, catalog } = this.state;

    if (language !== "en" && language !== prevState.language && !catalog.get(language)) {
      import("langs/" + language + ".json").then((module) => {
        this.setState({ catalog: catalog.set(language, module.default) });
      });
    }
  }

  onLanguageChange = (language) => {
    this.setState({ language });
  };

  onDateFormatChange = (e) => {
    this.setState({ dateFormat: e.target.value });
  };

  onMetricUnitsChange = () => {
    this.setState({ metricUnits: !this.state.metricUnits });
  };

  // also pops the locale modal onboarding tip
  setSwitcherPref = () => {
    const { setPreference, beforeClose } = this.props;
    setPreference(LOCALE_SWITCHER_PREF, true);
    beforeClose();
  };

  setLocaleAndClose = async () => {
    const { setLocale, currentUser, updateUser, setPreference, navigatorLanguage } = this.props;
    const { metricUnits, dateFormat } = this.state;

    // need to be performed in serial to avoid race condition since all prefs are assigned at once in backend
    await fetchJSON(`/set_locale?preferred_locale=${this.state.language}`, null, { method: "POST" });
    await setPreference("metric_units", metricUnits);
    await setPreference("date_format", dateFormat);
    await setLocale(this.state.language);
    if (currentUser.id) {
      await updateUser(currentUser.id, User.serialize(User({ metricUnits }), ["metricUnits"]));
    }
    if (navigatorLanguage) {
      this.setSwitcherPref();
    } // presence of navigatorLanguage implies this is onboarding
    window.location.reload();
  };

  onRequestClose = () => {
    const { navigatorLanguage, onRequestClose } = this.props;
    if (navigatorLanguage) this.setSwitcherPref();
    onRequestClose();
  };

  render() {
    const { navigatorLanguage, userLocale } = this.props;
    const { language, catalog, dateFormat, metricUnits } = this.state;

    // this is the only pattern i found that avoids [object object] in select
    // options. it's painful, and not as clean as being able to use
    // CustomFormattedMessage directly (for example)
    const tempIntl = createIntl(
      {
        locale: language,
        messages: language?.slice(0, 2) === "en" ? {} : catalog.get(language),
      },
      createIntlCache()
    );

    return (
      <ConfirmationDialog
        key={catalog}
        showCloseButton
        onRequestClose={this.onRequestClose}
        onCancel={this.onRequestClose}
        cancelText={fm(tempIntl, commonCTAs.cancel)}
        onConfirm={this.setLocaleAndClose}
        confirmText={fm(tempIntl, commonCTAs.confirm)}
        maxWidth="600px"
        title={fm(tempIntl, messages.title)}
      >
        {navigatorLanguage && (
          <div className={styles.switcherIntro}>
            <Suspense fallback={<></>}>
              <CustomFormattedMessage className={styles.firstMsg} locale={userLocale} message={messages.locAlert} />
              <CustomFormattedMessage locale={navigatorLanguage} message={messages.locAlert} />
            </Suspense>
          </div>
        )}

        <div className={styles.twoCol}>
          <RadioButtonGroup
            name="localeselect1"
            options={leftLangs}
            onChange={this.onLanguageChange}
            selected={language}
          />
          <RadioButtonGroup
            name="localeselect2"
            options={rightLangs}
            onChange={this.onLanguageChange}
            selected={language}
          />
        </div>

        <form>
          <div className={styles.twoCol}>
            <div>
              <label>{fm(tempIntl, messages.dateFormat)}</label>
              <select value={dateFormat} onChange={this.onDateFormatChange}>
                <option value="dmy">
                  {fm(tempIntl, messages.day)} / {fm(tempIntl, messages.month)} / {fm(tempIntl, messages.year)}
                </option>
                <option value="mdy">
                  {fm(tempIntl, messages.month)} / {fm(tempIntl, messages.day)} / {fm(tempIntl, messages.year)}
                </option>
                <option value="ymd">
                  {fm(tempIntl, messages.year)} / {fm(tempIntl, messages.month)} / {fm(tempIntl, messages.day)}
                </option>
              </select>
            </div>

            <div className={styles.inlineCB}>
              <input
                className={styles.cbInline}
                type="checkbox"
                checked={metricUnits}
                onChange={this.onMetricUnitsChange}
              />
              <label>{fm(tempIntl, messages.metricUnits)}</label>
            </div>
          </div>
        </form>
      </ConfirmationDialog>
    );
  }
}

const mapStateToProps = (state) => ({
  language: localeSelectors.language(state),
  currentUser: state.currentUser,
});

const mapDispatchToProps = (dispatch) => ({
  setLocale: (l) => dispatch(localeActions.setLocale(l)),
  updateUser: (id, params) => dispatch(userActions.update(id, params)),
  setPreference: (key, value) => dispatch(currenUserActions.setPreference(key, value)),
});

export const ChangeLocaleModal = connect(mapStateToProps, mapDispatchToProps)(_ChangeLocaleModal);
